import { useEffect, useLayoutEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { apiClient } from '@/api/http/client'
import { setCsrfToken } from '@/api/http/interceptors'
import { useSessionStore } from '@/stores/useSessionStore'
import { useMe } from '@/features/auth/api'
import {
  getPublicAuthApexHostname,
  IdentityAuthPath,
  POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT,
  AUTH_RESET_QUERY,
  abortApexPurge,
  beginApexPurge,
  isApexPurgeSignalAborted,
  readPostLogoutSkipAuthApexRedirectFlag,
  readTenantHandoffFailedFlag,
  clearTenantHandoffFailedFlag,
  clearLastTenantSubdomain,
} from '@/features/auth/lib'
import {
  canResumeLastTenantFromApex,
  tryResumeLastTenantFromApex,
} from '@/features/auth/lib/last-tenant-resume'
import { isPostInviteWelcomePending, postInviteWelcomeQuery } from '@/features/dashboard/lib/post-invite-welcome'

export function RedirectToPublicAuthApex() {
  const hostname = globalThis.location.hostname
  const apex = getPublicAuthApexHostname(hostname)

  useLayoutEffect(() => {
    const loc = globalThis.location
    const apexNow = getPublicAuthApexHostname(loc.hostname)
    if (apexNow === loc.hostname) {
      return
    }
    const portSuffix = loc.port ? `:${loc.port}` : ''
    loc.replace(
      `${loc.protocol}//${apexNow}${portSuffix}${loc.pathname}${loc.search}${loc.hash}`,
    )
  }, [])

  if (apex === hostname) {
    return <Outlet />
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  )
}

/**
 * For public auth routes (/login, forgot password, etc.): if the user already
 * has a valid session, redirect to home. Uses the same /me query as RequireAuth.
 *
 * Do **not** bounce tenant `/login` to the apex before session bootstrap — that
 * races post-login handoff (`_handoff` → cookies on the tenant host) and causes
 * an apex↔tenant refresh loop (me → handoff/create → bounce → repeat).
 * `RequireGuestBody` still normalizes unauthenticated tenant hosts to the apex
 * after `/me` settles (and after the post-logout skip).
 */
export function RequireGuest() {
  return <RequireGuestBody />
}

function RequireGuestBody() {
  const location = useLocation()
  const { isAuthenticated, user, explicitLogout, clearSession } = useSessionStore()
  const postLogoutBootstrapPending = readPostLogoutSkipAuthApexRedirectFlag()
  const { isLoading, isError } = useMe()
  const apexPurgeStartedRef = useRef(false)
  const authResetStartedRef = useRef(false)
  const [searchParams] = useSearchParams()
  const needsAuthReset = searchParams.get(AUTH_RESET_QUERY) === '1'

  /**
 * Unauthenticated public routes should use the auth apex host (e.g. acme.localhost → app.localhost).
 * After logout, keep `POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT` so `/me` stays disabled until login.
 */
  useLayoutEffect(() => {
    if (globalThis.window === undefined) return
    if (isAuthenticated && user && !postLogoutBootstrapPending) return

    const loc = globalThis.location
    const apex = getPublicAuthApexHostname(loc.hostname)
    if (apex === loc.hostname) return

    const bootstrappingGuest =
      !explicitLogout &&
      !postLogoutBootstrapPending &&
      !needsAuthReset &&
      isLoading
    if (bootstrappingGuest) return

    const { protocol, port, pathname, search, hash } = loc
    const portSuffix = port ? `:${port}` : ''
    globalThis.location.replace(
      `${protocol}//${apex}${portSuffix}${pathname}${search}${hash}`,
    )
  }, [
    isAuthenticated,
    user,
    explicitLogout,
    postLogoutBootstrapPending,
    isLoading,
    isError,
    needsAuthReset,
  ])

  /** After a failed tenant handoff, clear stale apex cookies before showing login. */
  useEffect(() => {
    if (!needsAuthReset || authResetStartedRef.current) return
    if (globalThis.window === undefined) return
    authResetStartedRef.current = true
    abortApexPurge()
    void (async () => {
      try {
        await apiClient.post(IdentityAuthPath.LOGOUT, undefined, {
          skipUnauthorizedRedirect: true,
        })
      } catch {
        // Best effort — session may already be absent.
      }
      setCsrfToken(null)
      clearSession()
      clearTenantHandoffFailedFlag()
      clearLastTenantSubdomain()
      try {
        globalThis.sessionStorage?.removeItem(POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT)
      } catch {}
      const loc = globalThis.location
      const params = new URLSearchParams(loc.search)
      params.delete(AUTH_RESET_QUERY)
      const nextSearch = params.size > 0 ? `?${params.toString()}` : ''
      globalThis.history.replaceState(null, '', `${loc.pathname}${nextSearch}`)
    })()
  }, [needsAuthReset, clearSession])

  /** Logout on tenant host does not clear host-only cookies on the public auth apex. */
  useEffect(() => {
    if (!postLogoutBootstrapPending || apexPurgeStartedRef.current) return
    if (globalThis.window === undefined) return
    if (useSessionStore.getState().authTransitionInProgress) return

    const apex = getPublicAuthApexHostname(globalThis.location.hostname)
    if (apex !== globalThis.location.hostname) return

    apexPurgeStartedRef.current = true
    const purgeSignal = beginApexPurge()
    void (async () => {
      if (
        isApexPurgeSignalAborted(purgeSignal) ||
        !readPostLogoutSkipAuthApexRedirectFlag()
      ) {
        return
      }
      try {
        await apiClient.post(IdentityAuthPath.LOGOUT, undefined, {
          skipUnauthorizedRedirect: true,
          signal: purgeSignal,
        })
      } catch {
        // Best effort — stale apex cookies may already be absent or purge was cancelled.
      }
      if (
        isApexPurgeSignalAborted(purgeSignal) ||
        !readPostLogoutSkipAuthApexRedirectFlag()
      ) {
        return
      }
      setCsrfToken(null)
      clearSession()
      clearLastTenantSubdomain()
    })()
  }, [postLogoutBootstrapPending, clearSession])

  useEffect(() => {
    if (!isError) return
    clearSession()
    try {
      globalThis.sessionStorage?.removeItem(POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT)
    } catch {}
  }, [isError, clearSession])

  const apexNeedsTenantResume =
    !postLogoutBootstrapPending &&
    !needsAuthReset &&
    !isAuthenticated &&
    !user &&
    (isError || !isLoading) &&
    canResumeLastTenantFromApex()

  /** Cold apex `/login` while the tenant host still has a session. */
  useLayoutEffect(() => {
    if (!apexNeedsTenantResume) return
    tryResumeLastTenantFromApex()
  }, [apexNeedsTenantResume])

  if (apexNeedsTenantResume) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return <Outlet />
  }

  if (postLogoutBootstrapPending && !isAuthenticated && !user) {
    return <Outlet />
  }

  if (
    !explicitLogout &&
    !postLogoutBootstrapPending &&
    !needsAuthReset &&
    !isAuthenticated &&
    !user &&
    isLoading
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (isAuthenticated && user) {
    if (postLogoutBootstrapPending || readTenantHandoffFailedFlag()) {
      return <Outlet />
    }
    const welcome = isPostInviteWelcomePending(new URLSearchParams(location.search))
    const search = welcome ? `?${postInviteWelcomeQuery()}` : ''
    return <Navigate to={{ pathname: '/', search }} replace />
  }

  return <Outlet />
}
